'use client'

// R2 wave 2 (redesigned): the personalized band on /dashboard. Logged out -> renders
// nothing. Logged in -> three distinct shapes so the band scans instead of reading:
//   Jump back in       — up to 3 large photo cards (recent views)
//   Your saved workflows — compact star-pills (navigation shortcuts, not cards)
//   Suggested for you   — up to 3 photo cards (merged into "For you" when recent is thin)
// Thumbnails ride content_index.image (enriched by sync-content-index.mjs).
import React, { useEffect, useState } from 'react'
import Link from 'next/link'
import { useSupabaseAuth } from '@/context/supabaseContext'
import { SUPABASE_CONFIGURED, supabaseBrowser } from '@/lib/supabase/client'
import {
  listBookmarks,
  listSavedWorkflows,
  recentlyViewed,
  suggestions,
  type ContentIndexRow,
  type PreferenceRow,
} from '@/lib/supabase/engagement'

const KIND_LABEL: Record<string, string> = {
  workflow: 'Workflow',
  knowledge_hub: 'Article',
  product: 'Product',
  vendor: 'Company',
  page: 'Page',
}

// per-type gradient fallbacks for the handful of rows without imagery
const KIND_FALLBACK: Record<string, string> = {
  workflow: 'from-blue-20 to-neutral-20',
  knowledge_hub: 'from-neutral-20 to-blue-20',
  product: 'from-neutral-40 to-neutral-20',
  vendor: 'from-neutral-40 to-neutral-20',
  page: 'from-neutral-40 to-neutral-20',
}

function PhotoCard({ item }: { item: ContentIndexRow }) {
  const label = KIND_LABEL[item.content_type] || item.content_type
  // logos and product-group shots are cutouts on white — show them whole (contain);
  // article/product photos keep the full-bleed crop (cover)
  const contain = item.content_type === 'vendor' || item.content_type === 'workflow'
  // stage titles carry a " — <workflow>" suffix; the subtitle already says it
  const displayTitle =
    item.workflow && item.title.endsWith(` — ${item.workflow}`)
      ? item.title.slice(0, -(item.workflow.length + 3))
      : item.title
  return (
    <Link
      href={item.content_path}
      className="group block overflow-hidden rounded-md border-[1px] border-solid border-subtle bg-neutral-100 no-underline transition-shadow hover:shadow-md"
    >
      <div className={`relative h-[150px] w-full overflow-hidden ${contain ? 'bg-white' : 'bg-neutral-95'}`}>
        {item.image ? (
          // plain <img>: media proxies via /api/media rewrite; content thumbs, not LCP
          // eslint-disable-next-line @next/next/no-img-element
          <img
            src={item.image}
            alt=""
            loading="lazy"
            className={`h-full w-full transition-transform duration-300 group-hover:scale-[1.03] ${contain ? (item.content_type === 'vendor' ? 'object-contain p-6' : 'object-contain p-3') : 'object-cover'}`}
          />
        ) : (
          <div
            className={`flex h-full w-full items-center justify-center bg-gradient-to-br ${KIND_FALLBACK[item.content_type] || KIND_FALLBACK.page}`}
          >
            <span className="select-none text-[64px] font-bold leading-none text-text-inverted opacity-30">
              {displayTitle.charAt(0)}
            </span>
          </div>
        )}
        <span className="absolute left-3 top-3 rounded-full bg-neutral-100 px-2.5 py-1 !text-label-XS-semibold uppercase tracking-wide text-text-subtle shadow-sm">
          {label}
        </span>
      </div>
      <div className="p-4">
        <div className="!text-label-S-semibold leading-snug text-text-base group-hover:text-blue-20">
          {displayTitle}
        </div>
        {item.workflow && item.content_type === 'workflow' && displayTitle !== item.workflow && (
          <div className="mt-1 !text-label-XS-regular text-text-faded">{item.workflow}</div>
        )}
      </div>
    </Link>
  )
}

export function DashboardPersonal() {
  const { user, isLoaded } = useSupabaseAuth()
  const [recent, setRecent] = useState<ContentIndexRow[]>([])
  const [saved, setSaved] = useState<PreferenceRow[]>([])
  const [favourites, setFavourites] = useState<PreferenceRow[]>([])
  const [suggested, setSuggested] = useState<ContentIndexRow[]>([])
  const [incomplete, setIncomplete] = useState(false)
  const [ready, setReady] = useState(false)

  useEffect(() => {
    if (!isLoaded || !user) return
    const profileCheck = supabaseBrowser()
      .from('profiles')
      .select('job_title,company')
      .eq('id', user.id)
      .maybeSingle()
    Promise.all([
      recentlyViewed(3),
      listSavedWorkflows(),
      suggestions(12),
      profileCheck,
      listBookmarks(),
    ]).then(
      ([r, s, g, p, b]) => {
        setRecent(r)
        setSaved(s)
        setFavourites(b)
        const seen = new Set([...r.map((x) => x.content_path), ...s.map((x) => x.content_path)])
        // image-first + variety: three near-identical cards (same workflow, same inherited
        // thumb) read as one — greedily prefer unseen images and unseen workflows
        const fresh = g.filter((x) => !seen.has(x.content_path))
        const ranked = [...fresh.filter((x) => x.image), ...fresh.filter((x) => !x.image)]
        const picked: ContentIndexRow[] = []
        for (const strict of [true, false]) {
          for (const c of ranked) {
            if (picked.length >= 3 || picked.includes(c)) continue
            const dupImage = c.image && picked.some((p) => p.image === c.image)
            const dupWorkflow = c.workflow && picked.some((p) => p.workflow === c.workflow)
            if (strict && (dupImage || dupWorkflow)) continue
            picked.push(c)
          }
        }
        setSuggested(picked)
        setIncomplete(!p.data?.job_title?.trim() || !p.data?.company?.trim())
        setReady(true)
      },
    )
  }, [isLoaded, user])

  if (!SUPABASE_CONFIGURED || !isLoaded || !user || !ready) return null

  const name =
    (user.user_metadata?.full_name as string | undefined) ||
    (user.user_metadata?.name as string | undefined) ||
    (user.email ? user.email.split('@')[0] : '')

  // thin history -> one merged row instead of two half-empty ones
  const merged = recent.length < 2
  const forYou = merged ? [...recent, ...suggested].slice(0, 3) : recent

  const Row = ({ title, items }: { title: string; items: ContentIndexRow[] }) =>
    items.length ? (
      <div className="mb-10">
        <h2 className="mb-4 text-xl font-bold">{title}</h2>
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
          {items.map((it) => (
            <PhotoCard key={it.content_path} item={it} />
          ))}
        </div>
      </div>
    ) : null

  return (
    <section className="mx-auto w-full max-w-[1200px] px-4 pb-8">
      {incomplete && (
        <Link
          href="/account?welcome=1"
          className="mb-10 block rounded-md border-[1px] border-solid border-blue-20 bg-neutral-100 p-4 no-underline hover:bg-neutral-95"
        >
          <div className="!text-label-S-semibold text-text-base">Finish filling out your profile →</div>
          <p className="mt-1 !text-label-XS-regular text-text-subtle">
            Add your role and company — it takes 20 seconds and unlocks posting in the community.
          </p>
        </Link>
      )}

      <Row title={merged ? 'For you' : 'Jump back in'} items={forYou} />

      {saved.length > 0 && (
        <div className="mb-10">
          <h2 className="mb-4 text-xl font-bold">Your saved workflows</h2>
          <div className="flex flex-wrap gap-2">
            {saved.map((s) => (
              <Link
                key={s.content_path}
                href={s.content_path}
                className="inline-flex items-center gap-2 rounded-full border-[1px] border-solid border-blue-20 bg-neutral-100 px-4 py-2 !text-label-XS-semibold text-blue-20 no-underline transition-colors hover:bg-blue-20 hover:text-text-inverted"
              >
                ★ {s.title || s.content_path}
              </Link>
            ))}
          </div>
        </div>
      )}

      {favourites.length > 0 && (
        <div className="mb-10">
          <h2 className="mb-4 text-xl font-bold">Your favourites</h2>
          <div className="flex flex-wrap gap-2">
            {favourites.map((f) => (
              <Link
                key={f.content_path}
                href={f.content_path}
                className="inline-flex items-center gap-2 rounded-full border-[1px] border-solid border-subtle bg-neutral-100 px-4 py-2 !text-label-XS-semibold text-text-base no-underline transition-colors hover:bg-neutral-95"
              >
                ♥ {f.title || f.content_path}
              </Link>
            ))}
          </div>
        </div>
      )}

      {!merged && <Row title="Suggested for you" items={suggested} />}
    </section>
  )
}
